import type {
  Project,
  ProjectTree,
  Flag,
  FlagWithEnvironments,
  Environment,
  Segment,
  ConfigVersion,
  Schedule,
  WebhookEndpoint,
  WebhookDelivery,
  OrgWebhookEndpoint,
  APIKey,
  OrgAPIKey,
  UserTarget,
  FlagDependency,
  SyncStatus,
  ConfigDiff,
  CreateProjectRequest,
  CreateFlagRequest,
  UpdateFlagRequest,
  ToggleFlagRequest,
  SetRolloutRequest,
  AddRuleRequest,
  UpdateRulesRequest,
  RollbackRequest,
  CreateScheduleRequest,
  CreateEnvironmentRequest,
  CreateCloudConnectionRequest,
  CloudConnection,
  TestConnectionResult,
  FlagDropIdentity,
  CreateSegmentRequest,
  CreateWebhookRequest,
  AddProjectMemberRequest,
  OrgMember,
  ProjectMember,
  AuditEvent,
  AuditLogSummary,
  Notification,
  OrgDetails,
  SubscriptionInfo,
  UpdateOrgRequest,
  UpdateOrgMemberRequest,
  UpdateOrgSettingsRequest,
  OrgSettings,
  OrgInvitation,
  InviteToOrgRequest,
  AdminOrgSummary,
  AdminOrgDetail,
  SystemHealthMetrics,
  ImpersonationStatus,
  ImpersonateResponse,
  AnalyticsOverview,
  AnalyticsFlagEval,
  AnalyticsTimeSeries,
  Promotion,
  PromotionFlagDiff,
  PromotionPreviewRequest,
  CreatePromotionRequest,
  ChangeRequest,
  CreateChangeRequestRequest,
} from './types'

function resolveApiUrl(): string {
  // Use explicit env var if set at build time
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL
  // Derive from current hostname: platform.flagdrop.io → api.flagdrop.io
  const host = window.location.hostname
  if (host.startsWith('platform.')) {
    return `${window.location.protocol}//api.${host.replace('platform.', '')}`
  }
  // Local dev fallback
  return 'http://localhost:8080'
}

const BASE_URL = resolveApiUrl()

export class ApiError extends Error {
  status: number
  body: unknown
  /** Error code from the API response body (e.g. "trial_expired", "not_found"). */
  code?: string

  constructor(status: number, fallbackMessage: string, body?: unknown) {
    // Prefer the API's structured error message over the generic "API error:
    // 500" fallback. Every FlagDrop API error response shape is
    // {"status":N,"code":"...","message":"..."} (see internal/platform/errors.go),
    // so when a JSON body is present we should surface it to users instead
    // of swallowing it. Forms downstream display err.message directly, so
    // this is the difference between "API error: 403 Forbidden" and
    // "Your 14-day trial has expired. Subscribe to continue."
    let resolvedMessage = fallbackMessage
    let resolvedCode: string | undefined
    if (body && typeof body === 'object') {
      const b = body as { message?: unknown; code?: unknown }
      if (typeof b.message === 'string' && b.message.trim()) {
        resolvedMessage = b.message
      }
      if (typeof b.code === 'string' && b.code.trim()) {
        resolvedCode = b.code
      }
    }
    super(resolvedMessage)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.code = resolvedCode
  }
}

// Get auth token directly from Clerk's global JS instance.
// No Vue reactivity or wiring needed — works regardless of component mount order.
async function getClerkToken(): Promise<string | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clerk = (window as any).Clerk
  if (!clerk) return null
  if (!clerk.loaded) {
    await new Promise<void>((resolve) => {
      const unsub = clerk.addListener(() => {
        if (clerk.loaded) { unsub?.(); resolve() }
      })
    })
  }
  if (!clerk.session) return null
  return clerk.session.getToken()
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const token = await getClerkToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    // Read the body as text once, then attempt to parse as JSON. Calling
    // res.json() then falling back to res.text() throws "body stream already
    // read" because fetch Response bodies can only be consumed once.
    const raw = await res.text()
    let errorBody: unknown = raw
    if (raw) {
      try {
        errorBody = JSON.parse(raw)
      } catch {
        // Not JSON — keep raw text body.
      }
    }
    throw new ApiError(
      res.status,
      `API error: ${res.status} ${res.statusText}`,
      errorBody,
    )
  }

  if (res.status === 204) {
    return undefined as T
  }

  return res.json()
}

// Projects
export const api = {
  // Projects
  listProjects: () => request<Project[]>('GET', '/api/v1/projects'),
  listProjectTree: () => request<ProjectTree[]>('GET', '/api/v1/projects?tree=true'),
  getProject: (slug: string) => request<Project>('GET', `/api/v1/projects/${slug}`),
  createProject: (data: CreateProjectRequest) =>
    request<Project>('POST', '/api/v1/projects', data),

  // Environments
  listEnvironments: (project: string) =>
    request<Environment[]>('GET', `/api/v1/projects/${project}/environments`),
  createEnvironment: (project: string, data: CreateEnvironmentRequest) =>
    request<Environment>('POST', `/api/v1/projects/${project}/environments`, data),
  updateEnvironment: (project: string, name: string, data: Partial<CreateEnvironmentRequest>) =>
    request<Environment>('PUT', `/api/v1/projects/${project}/environments/${name}`, data),

  // FlagDrop identity
  getFlagDropIdentity: () =>
    request<FlagDropIdentity>('GET', '/api/v1/flagdrop-identity'),

  // Cloud connections (per environment, 0..N)
  listCloudConnections: (project: string, envName: string) =>
    request<CloudConnection[]>('GET', `/api/v1/projects/${project}/environments/${envName}/cloud-connections`),
  createCloudConnection: (project: string, envName: string, data: CreateCloudConnectionRequest) =>
    request<CloudConnection>('POST', `/api/v1/projects/${project}/environments/${envName}/cloud-connections`, data),
  deleteCloudConnection: (project: string, envName: string, connID: string) =>
    request<void>('DELETE', `/api/v1/projects/${project}/environments/${envName}/cloud-connections/${connID}`),
  testCloudConnection: (project: string, envName: string, connID: string) =>
    request<TestConnectionResult>('POST', `/api/v1/projects/${project}/environments/${envName}/cloud-connections/${connID}/test`),

  // Flags
  listFlags: (project: string) =>
    request<FlagWithEnvironments[]>('GET', `/api/v1/projects/${project}/flags`),
  getFlag: (project: string, key: string) =>
    request<FlagWithEnvironments>('GET', `/api/v1/projects/${project}/flags/${key}`),
  createFlag: (project: string, data: CreateFlagRequest) =>
    request<Flag>('POST', `/api/v1/projects/${project}/flags`, data),
  updateFlag: (project: string, key: string, data: UpdateFlagRequest) =>
    request<Flag>('PUT', `/api/v1/projects/${project}/flags/${key}`, data),
  deleteFlag: (project: string, key: string) =>
    request<void>('DELETE', `/api/v1/projects/${project}/flags/${key}?confirm=true`),
  toggleFlag: (project: string, key: string, data: ToggleFlagRequest) =>
    request<void>('POST', `/api/v1/projects/${project}/flags/${key}/toggle`, data),
  setRollout: (project: string, key: string, data: SetRolloutRequest) =>
    request<void>('POST', `/api/v1/projects/${project}/flags/${key}/rollout`, data),
  // Set per-environment value override. Send `value: null` to clear the
  // override (env falls back to flag.defaultValue).
  setFlagValue: (project: string, key: string, data: { environment: string; value: unknown }) =>
    request<void>('POST', `/api/v1/projects/${project}/flags/${key}/value`, data),
  addRule: (project: string, key: string, data: AddRuleRequest) =>
    request<void>('POST', `/api/v1/projects/${project}/flags/${key}/rules`, data),
  updateRules: (project: string, key: string, data: UpdateRulesRequest) =>
    request<FlagWithEnvironments>('PUT', `/api/v1/projects/${project}/flags/${key}/rules`, data),

  // Flag Dependencies
  listDependencies: (project: string, flagKey: string) =>
    request<FlagDependency[]>('GET', `/api/v1/projects/${project}/flags/${flagKey}/dependencies`),
  addDependency: (project: string, flagKey: string, dependsOnKey: string) =>
    request<FlagDependency>('POST', `/api/v1/projects/${project}/flags/${flagKey}/dependencies`, { dependsOnKey }),
  removeDependency: (project: string, flagKey: string, depId: string) =>
    request<void>('DELETE', `/api/v1/projects/${project}/flags/${flagKey}/dependencies/${depId}`),
  listDependents: (project: string, flagKey: string) =>
    request<FlagDependency[]>('GET', `/api/v1/projects/${project}/flags/${flagKey}/dependents`),

  // User Targets
  listTargets: (project: string, key: string, envName: string) =>
    request<UserTarget[]>('GET', `/api/v1/projects/${project}/flags/${key}/environments/${envName}/targets`),
  addTarget: (project: string, key: string, envName: string, data: { identifier: string; identifierType?: string; serveValue: unknown }) =>
    request<UserTarget>('POST', `/api/v1/projects/${project}/flags/${key}/environments/${envName}/targets`, data),
  removeTarget: (project: string, key: string, envName: string, targetId: string) =>
    request<void>('DELETE', `/api/v1/projects/${project}/flags/${key}/environments/${envName}/targets/${targetId}`),

  // Segments
  listSegments: (project: string) =>
    request<Segment[]>('GET', `/api/v1/projects/${project}/segments`),
  createSegment: (project: string, data: CreateSegmentRequest) =>
    request<Segment>('POST', `/api/v1/projects/${project}/segments`, data),
  updateSegment: (project: string, name: string, data: Partial<CreateSegmentRequest>) =>
    request<Segment>('PUT', `/api/v1/projects/${project}/segments/${name}`, data),
  deleteSegment: (project: string, name: string) =>
    request<void>('DELETE', `/api/v1/projects/${project}/segments/${name}`),

  // Config push & sync
  pushConfig: (project: string, env: string) =>
    request<void>('POST', `/api/v1/projects/${project}/push/${env}`),
  getSyncStatus: (project: string) =>
    request<SyncStatus[]>('GET', `/api/v1/projects/${project}/sync-status`),
  rollback: (project: string, data: RollbackRequest) =>
    request<void>('POST', `/api/v1/projects/${project}/rollback`, data),

  // Panic
  panic: (project: string) =>
    request<void>('POST', `/api/v1/projects/${project}/panic`),

  // Schedules
  listSchedules: (project: string) =>
    request<Schedule[]>('GET', `/api/v1/projects/${project}/schedules`),
  createSchedule: (project: string, data: CreateScheduleRequest) =>
    request<Schedule>('POST', `/api/v1/projects/${project}/schedules`, data),
  cancelSchedule: (project: string, id: string) =>
    request<void>('DELETE', `/api/v1/projects/${project}/schedules/${id}`),

  // Webhooks
  listWebhooks: (project: string) =>
    request<WebhookEndpoint[]>('GET', `/api/v1/projects/${project}/webhooks`),
  createWebhook: (project: string, data: CreateWebhookRequest) =>
    request<WebhookEndpoint>('POST', `/api/v1/projects/${project}/webhooks`, data),
  listWebhookDeliveries: (project: string, webhookId: string) =>
    request<WebhookDelivery[]>('GET', `/api/v1/projects/${project}/webhooks/${webhookId}/deliveries`),
  testWebhook: (project: string, webhookId: string) =>
    request<WebhookDelivery>('POST', `/api/v1/projects/${project}/webhooks/${webhookId}/test`),

  // API Keys
  listApiKeys: (project: string) =>
    request<APIKey[]>('GET', `/api/v1/projects/${project}/api-keys`),
  createApiKey: (project: string, data: { name: string; scopes: string[]; expiresAt?: string }) =>
    request<APIKey & { key: string }>('POST', `/api/v1/projects/${project}/api-keys`, data),
  deleteApiKey: (project: string, id: string) =>
    request<void>('DELETE', `/api/v1/projects/${project}/api-keys/${id}`),
  rotateApiKey: (project: string, id: string) =>
    request<APIKey & { key: string }>('POST', `/api/v1/projects/${project}/api-keys/${id}/rotate`),

  // Versions
  listVersions: (project: string, env: string) =>
    request<ConfigVersion[]>('GET', `/api/v1/projects/${project}/versions/${env}`),
  getConfigDiff: (project: string, env: string) =>
    request<ConfigDiff>('GET', `/api/v1/projects/${project}/config-diff/${env}`),

  // Members
  listMembers: (project: string) =>
    request<(OrgMember & { projectRole?: string })[]>('GET', `/api/v1/projects/${project}/members`),
  addMember: (project: string, data: AddProjectMemberRequest) =>
    request<ProjectMember>('POST', `/api/v1/projects/${project}/members`, data),
  updateMemberRole: (project: string, memberId: string, data: { projectRole: string }) =>
    request<ProjectMember>('PUT', `/api/v1/projects/${project}/members/${memberId}`, data),
  removeMember: (project: string, memberId: string) =>
    request<void>('DELETE', `/api/v1/projects/${project}/members/${memberId}`),

  // Audit log
  listAuditLog: (project: string, params?: { limit?: number; before?: string; action?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.before) searchParams.set('before', params.before)
    if (params?.action) searchParams.set('action', params.action)
    const qs = searchParams.toString()
    return request<AuditEvent[]>('GET', `/api/v1/projects/${project}/audit-log${qs ? '?' + qs : ''}`)
  },

  // Audit log export (returns download URL for browser navigation)
  auditLogExportUrl: (project: string, params: { format: string; from?: string; to?: string }): string => {
    const searchParams = new URLSearchParams()
    searchParams.set('format', params.format)
    if (params.from) searchParams.set('from', params.from)
    if (params.to) searchParams.set('to', params.to)
    return `${BASE_URL}/api/v1/projects/${project}/audit-log/export?${searchParams.toString()}`
  },

  // Audit log compliance summary
  auditLogSummary: (project: string, params?: { from?: string; to?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.from) searchParams.set('from', params.from)
    if (params?.to) searchParams.set('to', params.to)
    const qs = searchParams.toString()
    return request<AuditLogSummary>('GET', `/api/v1/projects/${project}/audit-log/summary${qs ? '?' + qs : ''}`)
  },

  // Org API Keys (org-scoped, requires org_admin)
  listOrgApiKeys: () =>
    request<OrgAPIKey[]>('GET', '/api/v1/org/api-keys'),
  createOrgApiKey: (data: { name: string; scopes: string[]; expiresAt?: string }) =>
    request<OrgAPIKey & { key: string }>('POST', '/api/v1/org/api-keys', data),
  deleteOrgApiKey: (id: string) =>
    request<void>('DELETE', `/api/v1/org/api-keys/${id}`),
  rotateOrgApiKey: (id: string) =>
    request<OrgAPIKey & { key: string }>('POST', `/api/v1/org/api-keys/${id}/rotate`),

  // Org Webhooks (org-scoped, requires org_admin)
  listOrgWebhooks: () =>
    request<OrgWebhookEndpoint[]>('GET', '/api/v1/org/webhooks'),
  createOrgWebhook: (data: CreateWebhookRequest) =>
    request<OrgWebhookEndpoint & { secret: string }>('POST', '/api/v1/org/webhooks', data),
  deleteOrgWebhook: (id: string) =>
    request<void>('DELETE', `/api/v1/org/webhooks/${id}`),

  // Notifications (org-scoped)
  listNotifications: (params?: { limit?: number; unread?: boolean }) => {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.unread) searchParams.set('unread', 'true')
    const qs = searchParams.toString()
    return request<Notification[]>('GET', `/api/v1/notifications${qs ? '?' + qs : ''}`)
  },
  markNotificationRead: (id: string) =>
    request<void>('PUT', `/api/v1/notifications/${id}/read`),
  markAllNotificationsRead: () =>
    request<void>('POST', '/api/v1/notifications/read-all'),

  // Admin
  adminHealthCheck: async (): Promise<boolean> => {
    try {
      await request<{ status: string }>('GET', '/api/v1/admin/health')
      return true
    } catch {
      return false
    }
  },
  adminListOrgs: (search?: string) => {
    const params = new URLSearchParams()
    if (search) params.set('q', search)
    const qs = params.toString()
    return request<AdminOrgSummary[]>('GET', `/api/v1/admin/orgs${qs ? '?' + qs : ''}`)
  },
  adminGetOrgDetail: (orgId: string) =>
    request<AdminOrgDetail>('GET', `/api/v1/admin/orgs/${orgId}`),
  adminGetSystemHealth: () =>
    request<SystemHealthMetrics>('GET', '/api/v1/admin/health/metrics'),
  adminUpdatePlan: (orgId: string, plan: string) =>
    request<{ plan: string; message: string }>('PUT', `/api/v1/admin/orgs/${orgId}/plan`, { plan }),
  adminExtendTrial: (orgId: string, days: number) =>
    request<{ trialEndsAt: string; message: string }>('POST', `/api/v1/admin/orgs/${orgId}/extend-trial`, { days }),

  // Admin Impersonation
  adminStartImpersonation: (orgId: string) =>
    request<ImpersonateResponse>('POST', `/api/v1/admin/impersonate/${orgId}`),
  adminExitImpersonation: () =>
    request<{ message: string }>('GET', '/api/v1/admin/impersonate/exit'),
  adminGetImpersonationStatus: () =>
    request<ImpersonationStatus>('GET', '/api/v1/admin/impersonate/status'),

  // Billing
  getSubscription: () =>
    request<SubscriptionInfo>('GET', '/api/v1/billing/subscription'),
  createCheckout: (data: { plan: string; successUrl: string; cancelUrl: string }) =>
    request<{ url: string }>('POST', '/api/v1/billing/checkout', data),
  createPortal: (data: { returnUrl: string }) =>
    request<{ url: string }>('POST', '/api/v1/billing/portal', data),

  // Organization (org-scoped)
  getOrg: () =>
    request<OrgDetails>('GET', '/api/v1/org'),
  updateOrg: (data: UpdateOrgRequest) =>
    request<OrgDetails>('PUT', '/api/v1/org', data),
  listOrgMembers: () =>
    request<OrgMember[]>('GET', '/api/v1/org/members'),
  updateOrgMemberRole: (memberId: string, data: UpdateOrgMemberRequest) =>
    request<OrgMember>('PUT', `/api/v1/org/members/${memberId}`, data),
  removeOrgMember: (memberId: string) =>
    request<void>('DELETE', `/api/v1/org/members/${memberId}`),

  // Organization settings (allowed domains)
  getOrgSettings: () =>
    request<OrgSettings>('GET', '/api/v1/org/settings'),
  updateOrgSettings: (data: UpdateOrgSettingsRequest) =>
    request<OrgSettings>('PUT', '/api/v1/org/settings', data),

  // Organization invitations
  inviteToOrg: (data: InviteToOrgRequest) =>
    request<OrgInvitation>('POST', '/api/v1/org/invitations', data),
  listOrgInvitations: () =>
    request<{ data: OrgInvitation[] }>('GET', '/api/v1/org/invitations'),
  revokeOrgInvitation: (invitationId: string) =>
    request<void>('DELETE', `/api/v1/org/invitations/${invitationId}`),

  // Promotions
  previewPromotion: (project: string, data: PromotionPreviewRequest) =>
    request<PromotionFlagDiff[]>('POST', `/api/v1/projects/${project}/promotions/preview`, data),
  createPromotion: (project: string, data: CreatePromotionRequest) =>
    request<Promotion>('POST', `/api/v1/projects/${project}/promotions`, data),
  listPromotions: (project: string) =>
    request<Promotion[]>('GET', `/api/v1/projects/${project}/promotions`),
  getPromotion: (project: string, id: string) =>
    request<Promotion>('GET', `/api/v1/projects/${project}/promotions/${id}`),

  // Analytics
  getAnalyticsOverview: (project: string) =>
    request<AnalyticsOverview>('GET', `/api/v1/projects/${project}/analytics/overview`),
  getAnalyticsFlags: (project: string, params?: { period?: string; environment?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.environment) searchParams.set('environment', params.environment)
    const qs = searchParams.toString()
    return request<AnalyticsFlagEval[]>('GET', `/api/v1/projects/${project}/analytics/flags${qs ? '?' + qs : ''}`)
  },
  getAnalyticsFlagTimeSeries: (project: string, key: string, params?: { period?: string; granularity?: string; environment?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.granularity) searchParams.set('granularity', params.granularity)
    if (params?.environment) searchParams.set('environment', params.environment)
    const qs = searchParams.toString()
    return request<AnalyticsTimeSeries[]>('GET', `/api/v1/projects/${project}/analytics/flags/${key}${qs ? '?' + qs : ''}`)
  },

  // Change Requests (approval workflow)
  listChangeRequests: (project: string, params?: { status?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    const qs = searchParams.toString()
    return request<ChangeRequest[]>('GET', `/api/v1/projects/${project}/change-requests${qs ? '?' + qs : ''}`)
  },
  getChangeRequest: (project: string, id: string) =>
    request<ChangeRequest>('GET', `/api/v1/projects/${project}/change-requests/${id}`),
  createChangeRequest: (project: string, data: CreateChangeRequestRequest) =>
    request<ChangeRequest>('POST', `/api/v1/projects/${project}/change-requests`, data),
  approveChangeRequest: (project: string, id: string) =>
    request<ChangeRequest>('POST', `/api/v1/projects/${project}/change-requests/${id}/approve`),
  rejectChangeRequest: (project: string, id: string, reason: string) =>
    request<ChangeRequest>('POST', `/api/v1/projects/${project}/change-requests/${id}/reject`, { reason }),
  pendingChangeRequestCount: (project: string) =>
    request<{ count: number }>('GET', `/api/v1/projects/${project}/change-requests/pending-count`),
  setApprovalRequired: (project: string, envName: string, required: boolean) =>
    request<{ environment: string; approvalRequired: boolean }>('PUT', `/api/v1/projects/${project}/environments/${envName}/approval`, { required }),

  // Push history (Temporal workflow visibility)
  listPushHistory: (project: string) =>
    request<import('./types').PushHistoryEntry[]>('GET', `/api/v1/projects/${project}/push-history`),
  retryPush: (project: string) =>
    request<{ workflowId: string; runId: string; status: string }>('POST', `/api/v1/projects/${project}/push-retry`),

  // OAuth consent — the API stages a pending CLI authorization at
  // /authorize; this dashboard reads it via getOAuthPending and posts
  // the user's decision via approveOAuth. On success, approveOAuth
  // returns the URL to redirect the browser to (CLI's localhost
  // callback) with the authorization code already in the query string.
  getOAuthPending: (stateToken: string) =>
    request<OAuthPending>('GET', `/api/v1/oauth/pending?state_token=${encodeURIComponent(stateToken)}`),
  approveOAuth: (stateToken: string, decision: 'approve' | 'deny') =>
    request<OAuthApproveResponse>('POST', '/api/v1/oauth/approve', { stateToken, decision }),

}

// OAuth types — kept inline since they're only used by the consent page.
export interface OAuthPending {
  clientName: string
  clientId: string
  redirectUri: string
}

export interface OAuthApproveResponse {
  redirectUrl: string
}
